/**
 * A deploy flag's on/off reading: `1` or `true` (any case, surrounding spaces ignored) is on; anything else,
 * unset included, is off. The backend reads its flags the same way (`backend/src/lib/envFlag.ts`).
 */
export function isEnvFlagOn(value: string | undefined): boolean {
  const normalized = value?.trim().toLowerCase()
  return normalized === '1' || normalized === 'true'
}

/**
 * Whether the fal-key (BYOK) plans can be entered: the plan buttons on /pricing and /bar,
 * the "own fal key" switch on /select-plan, and the `/welcome?intent=fal` setup flow.
 * Off until the plans are purchasable (docs/byok-billing-plan.md, step 3); the pages then
 * show the plans without a way into the flow. Turned on with NEXT_PUBLIC_BYOK_PLANS_ENABLED.
 *
 * The literal `process.env.NEXT_PUBLIC_…` read is what Next inlines into the browser bundle.
 */
export function isByokPlansEnabled(): boolean {
  return isEnvFlagOn(process.env.NEXT_PUBLIC_BYOK_PLANS_ENABLED)
}

/**
 * Annual billing for the fal-key plans: the Monthly / Annual switch on /pricing and /select-plan, and
 * `period=annual` through fal setup into checkout (`byok_pro_annual`, `byok_studio_annual`; step 8 of
 * docs/byok-billing-plan.md). Shown only while the plans are on.
 */
export const BYOK_ANNUAL_ENABLED: boolean = true

/** How a fal-key plan is billed: monthly, or for the year at the annual price. */
export type FalPeriod = 'monthly' | 'annual'

/** Whether /pricing and /select-plan show the Monthly / Annual switch and the fal-key plans' annual prices. */
export function isByokAnnualEnabled(): boolean {
  return BYOK_ANNUAL_ENABLED && isByokPlansEnabled()
}

/** `/welcome?intent=fal` opens fal setup only while the plans are on; otherwise it is the plain welcome. */
export function isFalWelcomeIntent(intent: string | null): boolean {
  return intent === 'fal' && isByokPlansEnabled()
}

/** `/welcome?intent=fal&period=…`: annual only for `period=annual` while annual is on; monthly otherwise. */
export function falWelcomePeriod(period: string | null): FalPeriod {
  return period === 'annual' && isByokAnnualEnabled() ? 'annual' : 'monthly'
}
